<template>
  <div class="generic-faq-domains">
    <PageContent class="generic-faq-domains__content">
      <section class="generic-faq-domains__panel">
        <header class="generic-faq-domains__toolbar">
          <label class="generic-domain-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input v-model.trim="search" type="search" placeholder="Rechercher un domaine" aria-label="Rechercher un domaine">
          </label>
          <select v-model="statusFilter" class="generic-domain-status-filter" aria-label="Filtrer par état de la FAQ">
            <option value="">Tous les états</option>
            <option value="all">FAQ complète</option>
            <option value="partial">Partielle</option>
            <option value="none">Désactivée</option>
            <option value="module_disabled">Module inactif</option>
          </select>
          <GenericFaqTabs />
        </header>

        <div v-if="loading" class="generic-domain-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement des domaines…
        </div>
        <div v-else-if="loadError" class="generic-domain-state generic-domain-state--error">
          <span>{{ loadError }}</span>
          <button type="button" @click="initialize">Réessayer</button>
        </div>
        <div v-else class="generic-domain-table-scroll">
          <table class="generic-domain-table">
            <thead>
              <tr>
                <th><button type="button" @click="toggleSort('label')">Domaine <i :class="sortIcon('label')"></i></button></th>
                <th><button type="button" @click="toggleSort('application')">Application <i :class="sortIcon('application')"></i></button></th>
                <th class="generic-domain-table__center">Module FAQ</th>
                <th class="generic-domain-table__center"><button type="button" @click="toggleSort('activated_faqs')">Questions actives <i :class="sortIcon('activated_faqs')"></i></button></th>
                <th><button type="button" @click="toggleSort('status')">État <i :class="sortIcon('status')"></i></button></th>
                <th class="generic-domain-table__actions">Actions globales</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="domain in paginatedDomains" :key="domain.uuid">
                <td>
                  <strong>{{ domain.label }}</strong>
                  <small>{{ domain.slug }}</small>
                </td>
                <td>{{ domain.application.label || '—' }}</td>
                <td class="generic-domain-table__center">
                  <span class="generic-module-state" :class="{ 'generic-module-state--enabled': domain.module_enabled }">
                    {{ domain.module_enabled ? 'Actif' : 'Inactif' }}
                  </span>
                </td>
                <td class="generic-domain-table__center">
                  <strong>{{ domain.activated_faqs }}</strong> / {{ domain.total_faqs }}
                </td>
                <td>
                  <span class="generic-domain-status" :class="`generic-domain-status--${domain.status}`">
                    {{ statusLabel(domain.status) }}
                  </span>
                </td>
                <td class="generic-domain-table__actions">
                  <button
                    type="button"
                    class="generic-domain-action generic-domain-action--enable"
                    :disabled="!domain.module_enabled || domain.status === 'all' || Boolean(pendingUuid)"
                    title="Activer toutes les questions génériques"
                    @click="confirmActivation(domain, true)"
                  >
                    <i v-if="pendingUuid === domain.uuid && pendingAction === 'activate'" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <i v-else class="fas fa-check" aria-hidden="true"></i>
                    Activer tout
                  </button>
                  <button
                    type="button"
                    class="generic-domain-action generic-domain-action--disable"
                    :disabled="!domain.module_enabled || domain.status === 'none' || Boolean(pendingUuid)"
                    title="Désactiver toutes les questions génériques"
                    @click="confirmActivation(domain, false)"
                  >
                    <i v-if="pendingUuid === domain.uuid && pendingAction === 'deactivate'" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <i v-else class="fas fa-ban" aria-hidden="true"></i>
                    Désactiver tout
                  </button>
                </td>
              </tr>
              <tr v-if="!paginatedDomains.length">
                <td colspan="6" class="generic-domain-state">Aucun domaine ne correspond à votre recherche.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer v-if="!loading && !loadError" class="generic-domain-footer">
          <span>{{ rangeLabel }} · {{ totalFaqs }} questions génériques</span>
          <div v-if="totalPages > 1">
            <button type="button" :disabled="page === 1" @click="page--"><i class="fas fa-chevron-left" aria-hidden="true"></i></button>
            <span>Page {{ page }} / {{ totalPages }}</span>
            <button type="button" :disabled="page === totalPages" @click="page++"><i class="fas fa-chevron-right" aria-hidden="true"></i></button>
          </div>
        </footer>
      </section>
    </PageContent>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import GenericFaqTabs from '@/pages/dashboard/GenericFaqTabs.vue'
import {
  NOSDROITS_DOMAIN_UUID,
  selectInternalSourceDomain
} from '@/services/internalGenericContents.service'

export default {
  name: 'app-dashboard-generic-faq-domains',
  components: { PageContent, GenericFaqTabs },
  data () {
    return {
      domains: [],
      totalFaqs: 0,
      loading: true,
      loadError: '',
      search: '',
      statusFilter: '',
      sort: { key: 'label', direction: 'asc' },
      page: 1,
      perPage: 25,
      pendingUuid: null,
      pendingAction: null
    }
  },
  computed: {
    filteredDomains () {
      const search = this.search.toLocaleLowerCase('fr')
      const domains = this.domains.filter(domain => {
        if (this.statusFilter && domain.status !== this.statusFilter) return false
        if (!search) return true
        const haystack = `${domain.label || ''} ${domain.slug || ''} ${domain.application.label || ''}`.toLocaleLowerCase('fr')
        return haystack.includes(search)
      })
      const factor = this.sort.direction === 'asc' ? 1 : -1
      return domains.sort((left, right) => {
        const a = this.sortValue(left, this.sort.key)
        const b = this.sortValue(right, this.sort.key)
        if (a === b) return 0
        if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b, 'fr', { sensitivity: 'base' }) * factor
        return (a > b ? 1 : -1) * factor
      })
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.filteredDomains.length / this.perPage))
    },
    paginatedDomains () {
      const start = (this.page - 1) * this.perPage
      return this.filteredDomains.slice(start, start + this.perPage)
    },
    rangeLabel () {
      if (!this.filteredDomains.length) return '0 domaine'
      const start = (this.page - 1) * this.perPage + 1
      const end = Math.min(this.page * this.perPage, this.filteredDomains.length)
      return `${start}–${end} sur ${this.filteredDomains.length} domaines`
    }
  },
  watch: {
    search () { this.page = 1 },
    statusFilter () { this.page = 1 },
    totalPages (value) {
      if (this.page > value) this.page = value
    }
  },
  created () {
    this.initialize()
  },
  methods: {
    async initialize () {
      this.loading = true
      this.loadError = ''
      try {
        await selectInternalSourceDomain(this.$store, NOSDROITS_DOMAIN_UUID)
        await this.loadDomains()
      } catch (error) {
        this.loadError = 'Impossible de charger l’état des FAQ par domaine.'
        this.loading = false
      }
    },
    async loadDomains () {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await http.get('/internal/generic-contents/faq/domains')
        this.domains = Array.isArray(data.domains) ? data.domains : []
        this.totalFaqs = Number(data.total_faqs || 0)
      } catch (error) {
        this.loadError = 'Impossible de charger l’état des FAQ par domaine.'
      } finally {
        this.loading = false
      }
    },
    sortValue (domain, key) {
      if (key === 'application') return String(domain.application.label || '')
      if (key === 'activated_faqs') return Number(domain.activated_faqs || 0)
      return String(domain[key] || '')
    },
    toggleSort (key) {
      this.sort = {
        key,
        direction: this.sort.key === key && this.sort.direction === 'asc' ? 'desc' : 'asc'
      }
    },
    sortIcon (key) {
      if (this.sort.key !== key) return 'fas fa-sort'
      return this.sort.direction === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    statusLabel (status) {
      return {
        all: 'FAQ complète',
        partial: 'Activation partielle',
        none: 'FAQ désactivée',
        module_disabled: 'Module inactif'
      }[status] || '—'
    },
    confirmActivation (domain, activate) {
      const action = activate ? 'activer' : 'désactiver'
      this.$buefy.dialog.confirm({
        title: `${activate ? 'Activer' : 'Désactiver'} toute la FAQ ?`,
        message: `Voulez-vous ${action} les ${domain.total_faqs} questions génériques pour « ${domain.label} » ? Le client conservera ensuite la possibilité de régler chaque question individuellement.`,
        cancelText: 'Annuler',
        confirmText: activate ? 'Tout activer' : 'Tout désactiver',
        type: activate ? 'is-success' : 'is-danger',
        onConfirm: () => this.updateActivation(domain, activate)
      })
    },
    async updateActivation (domain, activate) {
      this.pendingUuid = domain.uuid
      this.pendingAction = activate ? 'activate' : 'deactivate'
      try {
        const url = `/internal/generic-contents/faq/domains/${domain.uuid}/activate-all`
        const { data } = activate ? await http.put(url) : await http.delete(url)
        const index = this.domains.findIndex(item => item.uuid === domain.uuid)
        if (index !== -1) this.$set(this.domains, index, data)
        this.$notify({
          group: 'maviepro-success',
          text: `Toutes les questions ont été ${activate ? 'activées' : 'désactivées'} pour ${domain.label}.`
        })
      } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.message
        this.$notify({ group: 'maviepro-error', text: message || 'Impossible de modifier les activations de ce domaine.' })
      } finally {
        this.pendingUuid = null
        this.pendingAction = null
      }
    }
  }
}
</script>

<style scoped>
.generic-faq-domains { background: #f4f6f9; height: 100%; min-height: 0; min-width: 0; overflow: hidden; width: 100%; }
.generic-faq-domains__content { display: flex; flex-direction: column; height: calc(100% - 40px); min-height: 0; }
.generic-faq-domains__panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; box-shadow: 0 4px 12px rgba(15, 23, 42, .06); display: flex; flex: 1 1 auto; flex-direction: column; max-width: 100%; min-height: 0; min-width: 0; overflow: hidden; }
.generic-faq-domains__toolbar { align-items: center; display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 18px 24px; padding: 22px 28px; }
.generic-domain-status-filter { background: #fff; border: 1px solid #d1d5db; border-radius: 9px; color: #374151; min-height: 44px; padding: 10px 32px 10px 12px; }
.generic-domain-search { align-items: center; border: 1px solid #d1d5db; border-radius: 9px; display: flex; flex: 1 1 260px; max-width: 360px; min-width: 240px; padding: 0 14px; }
.generic-domain-search:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, .12); }
.generic-domain-search i { color: #9ca3af; }
.generic-domain-search input { border: 0; box-shadow: none; min-width: 0; outline: none; padding: 11px; width: 100%; }
.generic-domain-table-scroll { contain: paint; flex: 1 1 auto; max-width: 100%; min-height: 0; min-width: 0; overflow: auto; width: 100%; }
.generic-domain-table { border-collapse: separate; border-spacing: 0; min-width: 1040px; table-layout: fixed; width: 100%; }
.generic-domain-table th { background: #f8fafc; color: #4b5563; font-size: 12px; font-weight: 700; letter-spacing: .03em; padding: 12px 14px; position: sticky; text-align: left; text-transform: uppercase; top: 0; vertical-align: top; z-index: 2; }
.generic-domain-table th button { align-items: center; background: transparent; border: 0; color: inherit; cursor: pointer; display: inline-flex; font: inherit; gap: 7px; letter-spacing: inherit; padding: 3px 0; text-align: left; text-transform: inherit; }
.generic-domain-table th button:hover, .generic-domain-table th button:focus-visible { color: #2563eb; }
.generic-domain-table th button:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
.generic-domain-table th button i { color: #9ca3af; }
.generic-domain-table td { border-top: 1px solid #edf0f3; box-sizing: border-box; color: #374151; height: 64px; line-height: 1.25rem; padding: 11px 14px; vertical-align: middle; }
.generic-domain-table tbody tr { height: 64px; }
.generic-domain-table tbody tr:hover { background: #f8fafc; }
.generic-domain-table td:first-child strong { color: #1f2937; display: block; }
.generic-domain-table td:first-child small { color: #94a3b8; display: block; margin-top: 2px; }
.generic-domain-table__center { text-align: center !important; }
.generic-domain-table__actions { text-align: right !important; width: 300px; }
.generic-module-state, .generic-domain-status { border-radius: 999px; display: inline-flex; font-size: 12px; font-weight: 600; padding: 5px 9px; }
.generic-module-state { background: #f1f5f9; color: #64748b; }
.generic-module-state--enabled { background: #dcfce7; color: #166534; }
.generic-domain-status--all { background: #dcfce7; color: #166534; }
.generic-domain-status--partial { background: #fef3c7; color: #92400e; }
.generic-domain-status--none { background: #fee2e2; color: #991b1b; }
.generic-domain-status--module_disabled { background: #f1f5f9; color: #64748b; }
.generic-domain-action { border-radius: 7px; font-size: 12px; font-weight: 600; margin-left: 7px; padding: 7px 10px; }
.generic-domain-action--enable { background: #fff; border: 1px solid #16a34a; color: #15803d; }
.generic-domain-action--disable { background: #fff; border: 1px solid #ef4444; color: #dc2626; }
.generic-domain-action:disabled { background: #f8fafc; border-color: #d1d5db; color: #9ca3af; cursor: not-allowed; }
.generic-domain-state { color: #6b7280; flex: 1 1 auto; padding: 48px !important; text-align: center !important; }
.generic-domain-state--error { color: #b91c1c; }
.generic-domain-state--error button { background: transparent; border: 0; color: #2563eb; margin-left: 8px; }
.generic-domain-footer { align-items: center; border-top: 1px solid #edf0f3; color: #64748b; display: flex; flex: 0 0 auto; font-size: 12px; justify-content: space-between; padding: 13px 24px; }
.generic-domain-footer > div { align-items: center; display: flex; gap: 10px; }
.generic-domain-footer button { background: #fff; border: 1px solid #d1d5db; border-radius: 6px; color: #475569; height: 30px; width: 32px; }
.generic-domain-footer button:disabled { color: #cbd5e1; }
@media (max-width: 1100px) {
  .generic-faq-domains__toolbar { align-items: stretch; flex-direction: column; }
  .generic-domain-search { max-width: none; min-width: 0; }
}
</style>
